import { useMemo } from 'react';
import { ColumnTable } from 'arquero';
import { CellPlotData, PinnedMorphotype } from 'redux/slices';
import { getColumn } from '../arrow/arqueroHelpers';

/**
 * Hook to enhance existing plot data with visual indicators for highlighted cluster-based morphotypes
 * and hovered Leiden clusters from the legend.
 * Performance optimization: No duplicate rendering - just modifies existing traces.
 * Memory-efficient: operates directly on DataFrame columns instead of creating object arrays
 * @param plotData - Existing plot data traces
 * @param pinnedCells - Array of pinned morphotypes from Redux store
 * @param highlightedLeidenCluster - The Leiden cluster value being hovered in the legend
 * @param dataFrame - DataFrame containing cell information to map cells to their Leiden cluster
 * @returns Enhanced plot data with modified markers for highlighted cluster cells
 */
export const useClusterOverlayTracesFromDataFrame = (
  plotData: CellPlotData[],
  pinnedCells?: PinnedMorphotype[],
  highlightedLeidenCluster?: string,
  dataFrame?: ColumnTable
): CellPlotData[] => {
  const highlightExtraSize = 2; // Additional width to add to highlighted cell markers
  const enhancedPlotData = useMemo(() => {
    if (!plotData.length) return plotData

    // Create a Set of cellIds that should be highlighted for fast lookup
    const highlightedCellIds = new Set<string>()

    // Find highlighted cluster-based morphotypes
    if (pinnedCells) {
      const highlightedClusters = pinnedCells.filter(
        (morphotype) =>
          morphotype.isClusterBased &&
          morphotype.isHighlighted &&
          morphotype.active &&
          morphotype.cells.points &&
          morphotype.cells.points.length > 0
      )

      highlightedClusters.forEach((morphotype) => {
        morphotype.cells.points?.forEach((point) => {
          if (point.cellId) {
            highlightedCellIds.add(point.cellId)
          }
        })
      })
    }

    // Add cells from hovered Leiden cluster in legend using DataFrame
    if (highlightedLeidenCluster && dataFrame) {
      // Extract columns directly from DataFrame (cached to prevent redundant .toArray() calls)
      const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
      const leidenColumn = getColumn<unknown>(dataFrame, 'leiden')

      if (cellIdColumn.length > 0 && leidenColumn.length > 0) {
        for (let i = 0; i < cellIdColumn.length; i += 1) {
          if (leidenColumn[i] !== undefined && leidenColumn[i] === highlightedLeidenCluster) {
            highlightedCellIds.add(cellIdColumn[i])
          }
        }
      }
    }

    // If no highlighted cells, return original data
    if (highlightedCellIds.size === 0) return plotData

    // Enhance each trace by adding borders to highlighted cells
    return plotData.map((trace) => {
      // Check if this trace has any cells that should be highlighted
      const hasHighlightedCells = trace.cellIds?.some((cellId) =>
        highlightedCellIds.has(cellId)
      )

      if (!hasHighlightedCells || trace.marker === undefined) {
        return trace // No changes needed
      }

      // Return enhanced trace with extra marker size
      const markerSize = trace.marker.size as number + highlightExtraSize

      return {
        ...trace,
        marker: {
          ...trace.marker,
          size: markerSize,
        },
      }
    })
  }, [plotData, pinnedCells, highlightedLeidenCluster, dataFrame])

  return enhancedPlotData
}

export default useClusterOverlayTracesFromDataFrame
